import { CommonModule } from '@angular/common'
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { AdminEventCardComponent } from '@app/modules/admin/components/shared/admin-event-card/admin-event-card.component'
import { EMPTY, merge, of, Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators'
import { AuthService } from '../../../../../auth/services/auth.service'
import { ServerRepresentedEvent } from '../../../../../events/models/event.model'
import { EventService } from '../../../../../events/services/event.service'
import { Persona } from '../../../../../personas/interfaces/persona.interface'
interface EventWithForms extends Partial<ServerRepresentedEvent> {
  detailedForms?: Persona[]
}

@Component({
  selector: 'ngx-badge-events-list',
  templateUrl: './badge-events-list.component.html',
  styleUrls: ['./badge-events-list.component.scss'],
  standalone: true,
  imports: [CommonModule, AdminEventCardComponent],
  providers: [EventService]
})
export class BadgeEventsListComponent implements OnInit, OnDestroy {
  @Output() eventSelected = new EventEmitter<EventWithForms>()

  public events: EventWithForms[] = []
  public selectedEventId: number | null = null
  public searchTerm: string = ''
  public isLoading: boolean = false
  public errorMessage: string = ''
  public hasWorkspaceError: boolean = false
  public page: number = 1
  public perPage: number = 10 // Match group forms perPage

  private _destroy$ = new Subject<void>()
  private _searchSubject = new Subject<string>()
  private _reload$ = new Subject<string>()

  constructor(public eventService: EventService, public authService: AuthService) {
    merge(this._searchSubject.pipe(debounceTime(300), distinctUntilChanged()), this._reload$)
      .pipe(
        switchMap((term) => {
          this.searchTerm = term
          return this._fetchEvents()
        }),
        takeUntil(this._destroy$)
      )
      .subscribe((response) => this._applyEvents(response))
  }

  ngOnInit(): void {
    this.checkWorkspaceAndLoadEvents()
  }

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
  }

  public checkWorkspaceAndLoadEvents(): void {
    if (!this.authService.currentWorkspace?.id) {
      this.hasWorkspaceError = true
      this.errorMessage = 'Workspace context lost. Please refresh the page.'
      return
    }
    this._loadEvents()
  }

  private _loadEvents(): void {
    this._reload$.next(this.searchTerm)
  }

  private _fetchEvents() {
    if (!this.authService.currentWorkspace?.id) {
      this.hasWorkspaceError = true
      this.errorMessage = 'Workspace context lost. Please refresh the page.'
      this.isLoading = false
      return EMPTY
    }

    this.hasWorkspaceError = false
    this.isLoading = true
    this.errorMessage = ''

    return this.eventService.getEvents(this.page, this.searchTerm, 0, false, this.perPage, 1).pipe(
      catchError((error) => {
        console.error('Badge Editor: Error loading events:', error)
        this.errorMessage = `Failed to load events: ${error.error?.message || error.message || 'Unknown error'}`
        return of({ data: [] as ServerRepresentedEvent[] })
      })
    )
  }

  private _applyEvents(response: { data?: ServerRepresentedEvent[] }): void {
    this.isLoading = false

    // First filter by date (matching group forms behavior exactly)
    const allEvents = response?.data || []

    // Filter to show only upcoming or live events (like group forms)
    const filteredEvents = allEvents.filter((event) => {
      if (!event || !event.public_start_date || !event.public_end_date) {
        return false
      }

      const now = new Date()
      const startDate = new Date(event.public_start_date)
      const endDate = new Date(event.public_end_date)

      // Show only upcoming or live events
      return now <= endDate // Event hasn't finished yet
    })
    if (!filteredEvents.length) {
      this.errorMessage = 'No upcoming or live events found. Create an event or check if your events have ended.'
      return
    }

    // Then filter for events that have forms (badge editor requirement)
    const eventsWithForms = filteredEvents.filter((event) => event.forms && event.forms.length > 0)
    if (!eventsWithForms.length) {
      this.errorMessage = 'No upcoming or live events with registration forms found. Add registration forms to your events to create badges.'
      return
    }
    this.events = eventsWithForms.sort((a, b) => {
      // Match group forms sorting: Live first, then Upcoming, then by start date
      const getEventStatus = (event: any) => {
        if (!event.public_start_date || !event.public_end_date) {
          return 'Unknown'
        }

        const now = new Date()
        const startDate = new Date(event.public_start_date)
        const endDate = new Date(event.public_end_date)

        if (now >= startDate && now <= endDate) {
          return 'Live' // Event is currently running
        } else if (now < startDate) {
          return 'Upcoming' // Event hasn't started yet
        } else {
          return 'Finished' // Event has ended
        }
      }

      const statusA = getEventStatus(a)
      const statusB = getEventStatus(b)

      // Live events first
      if (statusA === 'Live' && statusB !== 'Live') return -1
      if (statusB === 'Live' && statusA !== 'Live') return 1

      // Upcoming events second
      if (statusA === 'Upcoming' && statusB !== 'Upcoming') return -1
      if (statusB === 'Upcoming' && statusA !== 'Upcoming') return 1

      // If both have same status, sort by start date
      if (a.public_start_date && b.public_start_date) {
        const dateA = new Date(a.public_start_date).getTime()
        const dateB = new Date(b.public_start_date).getTime()
        return dateA - dateB // Closest start date first
      }

      return 0
    })

    if (this.events.length === 0) {
      if (filteredEvents.length === 0) {
        this.errorMessage = 'No upcoming or live events found. Create an event or check if your events have ended.'
      } else {
        this.errorMessage = 'No upcoming or live events with registration forms found. Add registration forms to your events to create badges.'
      }
    }
  }

  public onSearchTermChangedEvent(event: Event): void {
    const target = event.target as HTMLInputElement
    this._searchSubject.next(target.value)
  }

  public clearSearch(): void {
    this.searchTerm = ''
    this._searchSubject.next('')
  }

  public searchEvents(): void {
    this._loadEvents()
  }

  public selectEvent(event: EventWithForms): void {
    this.selectedEventId = event.id || null

    // Fetch detailed form information before emitting
    if (event.id) {
      this.isLoading = true
      this.eventService
        .getEventForms(event.id)
        .pipe(
          catchError((error) => {
            console.error('Error loading detailed forms:', error)
            // Fallback to basic event data if detailed forms fail
            this.eventSelected.emit(event)
            return of([])
          }),
          finalize(() => (this.isLoading = false)),
          takeUntil(this._destroy$)
        )
        .subscribe((detailedForms: Persona[]) => {
          const eventWithDetailedForms: EventWithForms = {
            ...event,
            detailedForms: detailedForms || [],
            forms: detailedForms || event.forms // Use detailed forms as primary, fallback to basic
          }
          this.eventSelected.emit(eventWithDetailedForms)
        })
    } else {
      this.eventSelected.emit(event)
    }
  }

  public getEventFormsCount(event: EventWithForms): number {
    return event.forms?.length || 0
  }

  public getStatusDisplay(status: number): { text: string; class: string } {
    switch (status) {
      case 1:
        return { text: 'Draft', class: 'status-draft' }
      case 2:
        return { text: 'Published', class: 'status-published' }
      case 3:
        return { text: 'Live', class: 'status-live' }
      case 4:
        return { text: 'Completed', class: 'status-completed' }
      case 5:
        return { text: 'Cancelled', class: 'status-cancelled' }
      default:
        return { text: 'Unknown', class: 'status-unknown' }
    }
  }
}
